import Big, {BigSource} from 'big.js';
import {combineLatest} from 'rxjs';
import {map, distinctUntilChanged, throttleTime} from 'rxjs/operators';
import {contractUtil} from '@/helper/contract';
import {eq, gt, max, minus, numUtil, plus, times, toFixed} from '@/helper/num';
import {calcMaintenanceMargin} from '@/helper/leverage';
import {calcUnrealisedPnlEv, calcValueRv} from '@/calc';
import {
  contractsCache$,
  leverageMarginsCache$,
  currenciesCurrencyCache$,
  contractDefaultTakerFeeRateEr$,
} from '@/config';
import {fiat$} from '@/state/fiat';
import {indexPricesCache$} from '@/price-index/price-index';
import {InnerContractAccount, innerContractAccounts$} from '@/contract-accounts/inner-contract-accounts';
import {contractMarketRealtimeCache$} from '@/market-contract/market24h-contract-cache';
import {riskUnitsCache$} from '@/ras/risk-unit-cache';
import {positionsCache$} from './positions-cache';
import type {ws, PriceEntity, FiatEntity, ContractPair, ContractMarket, LeverageMargin} from '@/types';
import type {RiskUnit} from '@/types/ws';

type ContractPositionRaw = ws.ContractPositionRaw;

const fiatIndexPriceEntity$ = combineLatest([fiat$, indexPricesCache$]).pipe(
  map(([fiat, indexPricesCache]) => indexPricesCache.get('.' + fiat)),
  distinctUntilChanged()
);
export const fiatEntity$ = combineLatest([fiat$, fiatIndexPriceEntity$]).pipe(
  map(([fiat, fiatIndexPriceEntity]) => convertFiatEntity(fiat, fiatIndexPriceEntity))
);

/**
 * 正向合约：全仓：ras && 非 classic 模式取ras，否则读aop、逐仓：读aop
 * 反向合约：全仓：读aop、逐仓：读aop
 */
export const marginRatioCache$ = combineLatest([
  riskUnitsCache$,
  positionsCache$,
  innerContractAccounts$,
  leverageMarginsCache$,
  contractsCache$,
  contractMarketRealtimeCache$,
  currenciesCurrencyCache$,
  contractDefaultTakerFeeRateEr$,
]).pipe(
  throttleTime(500),
  map(
    ([
      riskUnitsCache,
      positionsCache,
      innerContractAccounts,
      marginCache,
      contractsCache,
      contractMarketCache,
      currenciesCache,
      defaultTakerFeeRateEr,
    ]) => {
      const accountsCache = innerContractAccounts.data;
      const totalCollateralAvaiValueRv = innerContractAccounts.totalCollateralAvaiValueRv0;
      const riskUnits = riskUnitsCache.toArray<RiskUnit>();
      const positions = positionsCache.toArray<ContractPositionRaw>();
      const marginRatioCache = riskUnits
        .filter(({symbol, valuationCurrency}) => !symbol && ['USDT', 'USDC', 'MUSDT'].includes(valuationCurrency))
        .map(({valuationCurrency}) => {
          const {valueScale, valueFactor, valuePrecision} = currenciesCache.get(valuationCurrency);
          const scale = contractUtil.formatterValueScale(valuationCurrency, valueScale);
          const account = accountsCache.get(valuationCurrency);
          const totalEquityRv = calcCrossTotalEquityRv(
            account,
            totalCollateralAvaiValueRv,
            contractUtil.formatterValueFactor(valuationCurrency, valueFactor),
            valuePrecision
          );
          const maintenanceMarginRv = calcCrossPosMmCostV2(
            valuationCurrency,
            positions,
            marginCache,
            contractsCache,
            contractMarketCache,
            defaultTakerFeeRateEr
          );
          const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
          const maintenanceMarginRv2 = maintenanceMarginRv.gt(0) ? maintenanceMarginRv : Big(0);
          const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, maintenanceMarginRv);
          return {
            key: valuationCurrency + 'Cross',
            symbol: '',
            side: '',
            currency: valuationCurrency,
            totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
            maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
            marginRatioRr,
            marginRatio,
          };
        })
        .filter(Boolean)
        .reduce((res, cur) => res.set(cur.key, cur), new Map());

      const marginRatioCache2 = positions
        .filter(({type, size, isIsolated}) => type === 'PerpetualV2' && size > 0 && isIsolated)
        .map((position) => {
          const {symbol, posSide, currency} = position;
          if (!currenciesCache.has(currency)) {
            return null;
          }
          const {valueScale, valuePrecision} = currenciesCache.get(currency);
          const scale = contractUtil.formatterValueScale(currency, valueScale);
          const riskKey = symbol + posSide;
          const contract = contractsCache.get(position.symbol);
          const {mmCost} = calcNormalPosMMCost(
            position,
            marginCache,
            contractsCache,
            contractMarketCache,
            defaultTakerFeeRateEr
          );
          const market = contractMarketCache.get(position.symbol);
          const markPriceEp = market ? market.markPriceEp : 0;
          const unrealisedPnlRv = appendUnrealisedPnl(position, markPriceEp, contract);
          const totalEquityRv = Big(position.assignedPosBalanceEv).plus(unrealisedPnlRv);
          const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
          const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
          const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, mmCost);

          return {
            key: riskKey,
            symbol,
            side: position.side,
            currency: currency,
            totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
            maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
            marginRatioRr: Number(marginRatioRr),
            marginRatio,
          };
        })
        .filter(Boolean)
        .reduce((res, cur) => res.set(cur.key, cur), marginRatioCache);

      return positions
        .filter(({type}) => type === 'Perpetual')
        .map(({symbol, posSide, currency, isCross}) => {
          if (!contractsCache.has(symbol) || !currenciesCache.has(currency)) {
            return null;
          }
          const {contractSide, settleCurrency} = contractsCache.get(symbol);
          if (contractSide > 0) {
            return null;
          }
          const {valuePrecision, valueFactor} = currenciesCache.get(currency);
          if (positionsCache.has(symbol)) {
            const position = positionsCache.get(symbol);
            const contract = contractsCache.get(position.symbol);
            const {mmCost} = calcNormalPosMMCost(
              position,
              marginCache,
              contractsCache,
              contractMarketCache,
              defaultTakerFeeRateEr
            );
            const account = accountsCache.get(settleCurrency);
            const market = contractMarketCache.get(position.symbol);
            const markPriceEp = market ? market.markPriceEp : 0;
            const unrealisedPnlEv = appendUnrealisedPnl(position, markPriceEp, contract);

            const totalEquityRv = isCross
              ? calcCrossTotalEquityRv(
                  account,
                  totalCollateralAvaiValueRv,
                  contractUtil.formatterValueFactor(settleCurrency, valueFactor),
                  valuePrecision
                )
              : Big(position.assignedPosBalanceEv).plus(unrealisedPnlEv).div(valueFactor);

            const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
            const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
            const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv2, mmCost);

            return {
              key: symbol + (posSide || 'Merged'),
              symbol,
              side: position.side,
              currency,
              totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, 0),
              maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision),
              marginRatioRr: Number(marginRatioRr),
              marginRatio,
            };
          }
          return null;
        })
        .filter(Boolean)
        .reduce((res, cur) => res.set(cur.key, cur), marginRatioCache2);
    }
  )
);

export function calcAirdropMarginRatio(
  position,
  currenciesCache,
  marginCache,
  contractsCache,
  contractMarketCache,
  defaultTakerFeeRateEr
) {
  const {symbol, isCross, size, posMode, posSide} = position;
  if (size <= 0 || !contractsCache.has(symbol)) {
    return null;
  }
  const contract = contractsCache.get(position.symbol);
  const {contractSide, settleCurrency, type} = contract;
  if (contractSide < 0 || !currenciesCache.has(settleCurrency)) {
    return null;
  }
  const {valueScale, valuePrecision, valueFactor} = currenciesCache.get(settleCurrency);
  const scale = contractUtil.formatterValueScale(settleCurrency, valueScale);
  const accountBalanceEv = position.assignedPosBalanceEv;
  const key = symbol + (posSide || 'Merged') + '_' + position.userID;
  if (type === 'PerpetualV2') {
    let mmCost: any = {mmCost: 0};
    if (isCross) {
      if (posMode === 'OneWay') {
        mmCost = calcNormalPosMMCost(position, marginCache, contractsCache, contractMarketCache, defaultTakerFeeRateEr);
      } else if (posMode === 'Hedged') {
        const posLong = posSide === 'Long' ? position : null;
        const posShort = posSide === 'Short' ? position : null;
        mmCost = calcHedgedPosMM(
          posLong,
          posShort,
          marginCache,
          contractsCache,
          contractMarketCache,
          defaultTakerFeeRateEr
        );
      }
      const market = contractMarketCache.get(symbol);
      const contract = contractsCache.get(symbol);
      const markPriceEp = market ? market.markPriceEp : 0;
      const crossUnPnlRv = appendUnrealisedPnl(position, markPriceEp, contract);
      const totalEquityRv = Big(accountBalanceEv).plus(crossUnPnlRv);
      const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
      const maintenanceMarginRv = Big(mmCost.mmCost);
      const maintenanceMarginRv2 = maintenanceMarginRv.gt(0) ? maintenanceMarginRv : Big(0);
      const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, maintenanceMarginRv);
      return {
        key,
        symbol,
        totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
        maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
        marginRatioRr,
        marginRatio,
      };
    } else {
      const contract = contractsCache.get(position.symbol);
      const {mmCost} = calcNormalPosMMCost(
        position,
        marginCache,
        contractsCache,
        contractMarketCache,
        defaultTakerFeeRateEr
      );
      const market = contractMarketCache.get(position.symbol);
      const markPriceEp = market ? market.markPriceEp : 0;
      const unrealisedPnlRv = appendUnrealisedPnl(position, markPriceEp, contract);
      const totalEquityRv = Big(accountBalanceEv).plus(unrealisedPnlRv);
      const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
      const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
      const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv, mmCost);

      return {
        key,
        symbol,
        side: position.side,
        totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, scale),
        maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision, scale),
        marginRatioRr: Number(marginRatioRr),
        marginRatio,
      };
    }
  } else if (type === 'Perpetual') {
    const {mmCost} = calcNormalPosMMCost(
      position,
      marginCache,
      contractsCache,
      contractMarketCache,
      defaultTakerFeeRateEr
    );
    const market = contractMarketCache.get(position.symbol);
    const markPriceEp = market ? market.markPriceEp : 0;
    const unrealisedPnlEv = appendUnrealisedPnl(position, markPriceEp, contract);

    const totalEquityRv = isCross
      ? Big(accountBalanceEv)
          .plus(unrealisedPnlEv)
          .minus(position.orderCostEv || 0)
          .minus(position.estimatedOrdLossEv || 0)
          .div(valueFactor)
      : Big(position.assignedPosBalanceEv).plus(unrealisedPnlEv).div(valueFactor);
    const totalEquityRv2 = totalEquityRv.gt(0) ? totalEquityRv : Big(0);
    const maintenanceMarginRv2 = Big(mmCost).gt(0) ? mmCost : Big(0);
    const {marginRatioRr, marginRatio} = calcMarginRatio(totalEquityRv2, mmCost);

    return {
      key,
      symbol,
      totalEquity: numUtil.floor(totalEquityRv2, valuePrecision, 0),
      maintenanceMargin: numUtil.floor(maintenanceMarginRv2, valuePrecision),
      marginRatioRr: Number(marginRatioRr),
      marginRatio,
    };
  }
  return null;
}

function convertFiatEntity(fiat: string, fiatIndexPriceEntity: PriceEntity): FiatEntity {
  if (fiatIndexPriceEntity) {
    const {price, priceEp} = fiatIndexPriceEntity;
    return {fiat, price, priceEp};
  }
  return null;
}

function appendUnrealisedPnl(position, exitPriceEp, contract) {
  if (!contract) {
    return 0;
  }
  const {contractSide, contractSize, valueFactor, priceFactor} = contract;
  return calcUnrealisedPnlEv({
    side: position.side,
    contractSide,
    contractSize,
    qty: position.size,
    entryPriceEp: position.avgEntryPriceEp,
    exitPriceEp,
    factor: valueFactor,
    priceFactor,
  });
}

function genMM(position, marginCache, contractsCache, markPriceEp) {
  if (!position) {
    return 0;
  }
  const {symbol} = position;
  const contract = contractsCache.get(symbol);
  const {maintenanceMargin} = calcMaintenanceMargin(contract, {...position, markPriceEp}, marginCache);
  return maintenanceMargin || 0;
}

export function calcHedgedPosMM(
  posLong: ContractPositionRaw,
  posShort: ContractPositionRaw,
  marginCache: Map<string, LeverageMargin>,
  contractsCache: Map<string, ContractPair>,
  contractMarketCache: Map<string, ContractMarket>,
  defaultTakerFeeRateEr: number
) {
  if ((!posLong && !posShort) || (posLong?.size <= 0 && posShort?.size <= 0)) {
    return {
      mm: 0,
      posValue: 0,
      mmCost: 0,
    };
  }
  const longSize = posLong?.size || 0;
  const shortSize = posShort?.size || 0;

  const morePosition = longSize > shortSize ? posLong : posShort;
  const lessPosition = longSize > shortSize ? posShort : posLong;

  const markPriceEp = getMarkPriceEp(morePosition?.symbol, contractMarketCache);
  const morePosMM = morePosition ? genMM(morePosition, marginCache, contractsCache, markPriceEp) : 0;
  const lessPosMM = lessPosition ? genMM(lessPosition, marginCache, contractsCache, markPriceEp) : 0;

  const mm = Big(morePosMM).minus(lessPosMM);
  const posValue = minus(times(morePosition?.size || 0, markPriceEp), times(lessPosition?.size || 0, markPriceEp));
  const contract = contractsCache.get(posLong?.symbol || posShort?.symbol);

  const closePosFee = calcClosePosDefaultFee(
    contract.contractSide,
    morePosition.posSide === 'Long',
    posValue,
    mm,
    defaultTakerFeeRateEr
  );

  return {
    mm,
    posValue,
    mmCost: plus(closePosFee, mm),
  };
}

export function calcNormalPosMMCost(
  position: ContractPositionRaw,
  marginCache: Map<string, LeverageMargin>,
  contractsCache: Map<string, ContractPair>,
  contractMarketCache: Map<string, ContractMarket>,
  defaultTakerFeeRateEr: number
) {
  if (!position || position.size <= 0) {
    return {
      mm: 0,
      posValue: 0,
      mmCost: 0,
    };
  }

  const markPriceEp = getMarkPriceEp(position.symbol, contractMarketCache);
  const mm = genMM(position, marginCache, contractsCache, markPriceEp);
  const contract = contractsCache.get(position.symbol);
  const posValue = calcValueRv(
    contract.contractSide,
    contract.contractSize,
    contract.priceFactor,
    position.size,
    markPriceEp
  );
  const closePosFee = calcClosePosDefaultFee(
    contract.contractSide,
    position.side === 'Buy',
    posValue,
    mm,
    contractUtil.getDefaultTakerFeeRate(contract, defaultTakerFeeRateEr)
  );
  return {
    mm,
    posValue,
    mmCost: plus(closePosFee, mm),
  };
}

function calcMarginRatio(totalEquityRv: Big, mmCost: BigSource) {
  const mmCost2 = Big(mmCost || 0);
  const rawMR = totalEquityRv.gt(0) && mmCost2.gt(0) ? mmCost2.div(totalEquityRv) : Big(mmCost2.gt(0) ? 1 : 0);
  const marginRatioRr = rawMR.gt(1) ? 1 : Number(rawMR);
  const marginRatio = numUtil.floor(marginRatioRr, 2, -2) + '%';
  return {marginRatioRr, marginRatio};
}

function calcClosePosDefaultFee(contractSide, isLong, posValue, maintenanceMarginRv, defaultTakerFeeRate) {
  if ((contractSide > 0 && isLong) || (contractSide < 0 && !isLong)) {
    return Big(posValue).minus(maintenanceMarginRv).times(defaultTakerFeeRate);
  }
  return Big(posValue).plus(maintenanceMarginRv).times(defaultTakerFeeRate);
}

function getMarkPriceEp(symbol, marketCache) {
  const market = marketCache.get(symbol);
  return market ? market.markPriceEp : 0;
}

/**
 * 计算全仓Equity
 * @param account
 * @param totalCollateralAvaiValueRv 剩余可抵押资产折合的总价值
 * @returns
 */
function calcCrossTotalEquityRv(
  account: InnerContractAccount,
  totalCollateralAvaiValueRv,
  valueFactor,
  valuePrecision
) {
  if (account) {
    /**
     * 支持抵押开仓，超出本金占用的金额: CcyRiskFrozenAsCollateralValue = abs(min(0, canBorrow#CcyRiskCrossAvailableEquityValue)))
     * 剩余总可借金额: totalCollateralAvaiValueRv = sum(CcyRiskCrossAvailableAsCollateralValue) - sum(CcyRiskFrozenAsCollateralValue) (有可能为负数)
     * 剩余总可借折合当前资产数量: totalCollateralAvaiBalance = totalCollateralAvaiValueRv / price
     *
     * CcyRiskCrossAvailableEquity = CcyRiskCrossEquity -  CcyTotalCrossPosUsedBalance - CcyTotalOrderUsedBalance
     * => CcyRiskCrossEquity - CcyTotalOrderUsedBalance = CcyRiskCrossAvailableEquity + CcyTotalCrossPosUsedBalance
     *
     * 公式：sum(CcyRiskCrossAvailableAsCollateralInUSDT) + CcyRiskCrossEquity - CcyTotalOrderUsedBalance
     * 等价于: totalCollateralAvaiBalance + abs(min(0, canBorrow#CcyRiskCrossAvailableEquity))) + CcyRiskCrossEquity - CcyTotalOrderUsedBalance
     * => totalCollateralAvaiBalance + abs(min(0, canBorrow#CcyRiskCrossAvailableEquity))) + CcyRiskCrossAvailableEquity + CcyTotalCrossPosUsedBalance
     *
     * =>
     *    CcyRiskCrossAvailableEquity > 0 => totalCollateralAvaiBalance + CcyRiskCrossAvailableEquity + CcyTotalCrossPosUsedBalance
     *    不可借CcyRiskCrossAvailableEquity < 0 => totalCollateralAvaiBalance + CcyRiskCrossAvailableEquity + CcyTotalCrossPosUsedBalance
     *    可借CcyRiskCrossAvailableEquity < 0 => totalCollateralAvaiBalance -  CcyRiskCrossAvailableEquity + CcyRiskCrossAvailableEquity + CcyTotalCrossPosUsedBalance
     *                                       => totalCollateralAvaiBalance + CcyTotalCrossPosUsedBalance
     */

    const borrowAvai =
      account.canBorrow && !eq(totalCollateralAvaiValueRv || 0, 0) && gt(account.indexPriceRp, 0)
        ? toFixed(Big(totalCollateralAvaiValueRv).div(account.indexPriceRp).times(valueFactor), valuePrecision, 3)
        : '0';
    const crossAvailableEquityEv = account.canBorrow
      ? max(account.crossAvailableEquityEv || 0, 0)
      : account.crossAvailableEquityEv || 0;
    return Big(crossAvailableEquityEv)
      .plus(account.totalCrossPosCostEv || 0)
      .plus(account.crossFixedUsedBalanceEv || 0)
      .plus(borrowAvai)
      .minus(account.isPerpCollateral ? account.frozenAsCollateralEv || 0 : 0)
      .div(valueFactor);
  }
  return Big(0);
}

export function calcCrossPosMmCostV2(
  accountCurrency,
  positions,
  marginCache,
  contractsCache,
  contractMarketCache,
  defaultTakerFeeRateEr
) {
  const crossPositions = positions.filter(({isCross, size, type, currency}) => {
    return isCross && size > 0 && type === 'PerpetualV2' && currency === accountCurrency;
  });

  const allCrossOneWayMM = crossPositions
    .filter(({posMode}) => posMode === 'OneWay')
    .map((position) => {
      const {mmCost} = calcNormalPosMMCost(
        position,
        marginCache,
        contractsCache,
        contractMarketCache,
        defaultTakerFeeRateEr
      );
      return mmCost;
    })
    .reduce((p, c) => p.plus(c), Big(0));

  const hedgedPositions = crossPositions.filter(({posMode}) => posMode === 'Hedged');
  const hedgedSymbols = Array.from(new Set(hedgedPositions.map(({symbol}) => symbol)));
  return hedgedSymbols
    .map((symbol) => {
      const curPositions = hedgedPositions.filter((item) => item.symbol === symbol);
      const posLong = curPositions.find(({posSide}) => posSide === 'Long');
      const posShort = curPositions.find(({posSide}) => posSide === 'Short');
      const {mmCost} = calcHedgedPosMM(
        posLong,
        posShort,
        marginCache,
        contractsCache,
        contractMarketCache,
        defaultTakerFeeRateEr
      );
      return mmCost;
    })
    .reduce((p, c) => p.plus(c), allCrossOneWayMM);
}
